import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { AuthContext } from '../../../../Header/auth - context';
import './FirmaSef.css';

/* SEF podešavanja po firmi — API ključ, datum od kog se povlače ulazne fakture
   i rok za odluku. Podaci žive u sef.Konfiguracija (16.09.2026).

   Ekran vide samo direktor i eskaliran — provera je na ruti
   (requireDirektorIliEskaliran) i u samoj proceduri.

   Tri stanja, i svako isključuje ostala:
     1. firma nije iz Srbije  → SEF se NE MOŽE aktivirati (državni sistem Srbije)
     2. Srbija, modul isključen → može, ali nije u pretplati
     3. Srbija, modul uključen  → forma radi */
const FirmaSef = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const toast = useRef(null);

    const [podaci, setPodaci] = useState({ apiKey: '' });
    const [stanje, setStanje] = useState({
        imaKljuc: false, jeSrbija: true, drzava: null,
        datumIzmene: null, izmenio: null,
    });
    const [nemaPrava, setNemaPrava] = useState(false);
    const [ucitava, setUcitava] = useState(true);
    const [cuva, setCuva] = useState(false);
    /* Ključ je tajna — podrazumevano maskiran, otkriva se namernim klikom. */
    const [prikaziKljuc, setPrikaziKljuc] = useState(false);

    const zaglavlja = () => ({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + auth.token,
    });

    const poziv = async (putanja, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/sef/' + putanja, {
            method: 'POST', headers: zaglavlja(), body: JSON.stringify(telo),
        });
        const tekst = await r.text();
        const d = tekst ? JSON.parse(tekst) : {};
        if (r.status === 403) { const e = new Error(d.message || ''); e.zabranjeno = true; throw e; }
        if (!r.ok) throw new Error(d.message || t('common.greska'));
        return d;
    };

    const ucitaj = () => {
        setUcitava(true);
        poziv('Konfiguracija')
            .then((d) => {
                setPodaci({ apiKey: d.ApiKey || '' });
                setStanje({
                    imaKljuc: d.ImaKljuc === 1,
                    jeSrbija: d.JeSrbija === 1,
                    drzava: d.DrzavaFirme,
                    datumIzmene: d.DatumIzmene,
                    izmenio: d.IzmenioKorisnik,
                });
                setNemaPrava(false);
            })
            .catch((e) => {
                if (e.zabranjeno) setNemaPrava(true);
                else toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
            })
            .finally(() => setUcitava(false));
    };

    useEffect(() => { ucitaj(); /* eslint-disable-next-line */ }, []);

    const sacuvaj = () => {
        setCuva(true);
        poziv('KonfiguracijaSave', { apiKey: podaci.apiKey || null })
            .then((r) => {
                toast.current?.show({ severity: 'success', summary: t('common.sacuvano'), detail: r.message, life: 3000 });
                ucitaj();
            })
            .catch((e) => toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 }))
            .finally(() => setCuva(false));
    };

    /* ── stanja koja isključuju formu ── */
    const okvir = (klasa, ikona, naslov, tekst) => (
        <div className="sef-stranica">
            <Toast ref={toast} />
            <div className={`sef-kartica sef-kartica--poruka ${klasa}`}>
                <i className={`pi ${ikona} sef-poruka-ikona`} />
                <p className="sef-poruka-naslov">{naslov}</p>
                <p className="sef-poruka-tekst">{tekst}</p>
            </div>
        </div>
    );

    if (nemaPrava)
        return okvir('zabrana', 'pi-lock', t('sefKonf.zabranaNaslov'), t('sefKonf.samoDirektor'));

    /* Firma van Srbije: nije stvar pretplate nego zemlje — SEF je srpski
       državni sistem i ovoj firmi se ne može ni uključiti. Forma se ne
       prikazuje uopšte, jer ovde nema šta da se podesi. */
    if (!ucitava && !stanje.jeSrbija)
        return okvir('nedostupno', 'pi-globe', t('sefKonf.nijeSrbijaNaslov'),
            t('sefKonf.nijeSrbija', { drzava: stanje.drzava || '—' }));

    /* Stanje "Nije u pretplati" je otpalo sa kolonom firme_subscription.SEF —
       unet ključ JESTE aktivacija, pa ostaju samo dva stanja. */
    const stanjePilula = stanje.imaKljuc
        ? { klasa: 'st-aktivan', tekst: t('sefKonf.stanjeAktivan') }
        : { klasa: 'st-cekaKljuc', tekst: t('sefKonf.stanjeBezKljuca') };

    return (
        <div className="sef-stranica">
            <Toast ref={toast} />

            <div className="sef-kartica">
                <div className="sef-zaglavlje">
                    <div className="sef-znak"><i className="pi pi-cloud" /></div>
                    <div className="sef-zaglavlje-tekst">
                        <p className="sef-naslov">{t('sefKonf.naslov')}</p>
                        <p className="sef-opis">{t('sefKonf.opis')}</p>
                    </div>
                    {!ucitava && <span className={`sef-stanje ${stanjePilula.klasa}`}>{stanjePilula.tekst}</span>}
                </div>

                <div className="sef-polja">
                    <div className="sef-polje">
                        <label htmlFor="sefApiKey">{t('sefKonf.apiKljuc')}</label>
                        <div className="sef-kljuc">
                            <InputText id="sefApiKey"
                                type={prikaziKljuc ? 'text' : 'password'}
                                value={podaci.apiKey}
                                autoComplete="off" spellCheck="false"
                                placeholder={stanje.imaKljuc ? t('sefKonf.phKljucUnet') : t('sefKonf.phKljuc')}
                                onChange={(e) => setPodaci((p) => ({ ...p, apiKey: e.target.value }))} />
                            <Button type="button" icon={prikaziKljuc ? 'pi pi-eye-slash' : 'pi pi-eye'}
                                className="p-button-text sef-oko"
                                aria-label={prikaziKljuc ? t('sefKonf.sakrij') : t('sefKonf.prikazi')}
                                onClick={() => setPrikaziKljuc((v) => !v)} />
                        </div>
                        <small>{t('sefKonf.apiKljucPomoc')}</small>
                    </div>

                </div>

                <div className="sef-podnozje">
                    <span className="sef-meta">
                        {stanje.datumIzmene
                            ? t('sefKonf.poslednjaIzmena', { datum: stanje.datumIzmene, korisnik: stanje.izmenio || '—' })
                            : t('sefKonf.nikadMenjano')}
                    </span>
                    <Button label={t('common.sacuvaj')} icon="pi pi-save"
                        className="p-button-success"
                        onClick={sacuvaj} loading={cuva} disabled={ucitava} />
                </div>
            </div>
        </div>
    );
};

export default FirmaSef;
